import React, { useMemo } from 'react';
import { Stage, Layer, Line, Group, Rect, Arrow } from 'react-konva';
import type Konva from 'konva';
import type { KonvaMouseEvent, KonvaWheelEvent, ThemePalette } from '../../types';
import { CANVAS } from '../../constants';

const GRID_SIZE = CANVAS.GRID_SIZE;

interface Viewport {
  x: number;
  y: number;
  scale: number;
}

interface CanvasStageProps {
  stageRef: React.RefObject<Konva.Stage>;
  viewport: Viewport;
  windowSize: { width: number; height: number };
  palette: ThemePalette;
  stageCursor: string;
  marqueeRect: { x: number; y: number; w: number; h: number } | null;
  lassoPoints?: number[] | null;
  frameDrawRect: { x: number; y: number; w: number; h: number } | null;
  drawRectKind?: 'frame' | 'box';
  markupStroke: { x1: number; y1: number; x2: number; y2: number; kind: 'arrow' | 'line' } | null;
  onMouseDown: (e: KonvaMouseEvent) => void;
  onMouseMove: (e: KonvaMouseEvent) => void;
  onMouseUp: (e: KonvaMouseEvent) => void;
  onMouseLeave: () => void;
  onClick: (e: KonvaMouseEvent) => void;
  onWheel: (e: KonvaWheelEvent) => void;
  children?: React.ReactNode;
}

/**
 * Wraps the Konva Stage, Layer, viewport Group, grid lines, and selection/frame-draw
 * overlay rects. Children (FlowCanvas, SequenceCanvas) are rendered inside the
 * viewport-transformed Group.
 */
export const CanvasStage = React.forwardRef<HTMLDivElement, CanvasStageProps>(
  function CanvasStage(
    {
      stageRef,
      viewport,
      windowSize,
      palette,
      stageCursor,
      marqueeRect,
      lassoPoints,
      frameDrawRect,
      drawRectKind = 'frame',
      markupStroke,
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave,
      onClick,
      onWheel,
      children,
    },
    ref,
  ) {
    // ── Viewport-aware grid ─────────────────────────────────────────────────
    const gridLines = useMemo(() => {
      const { x: vx, y: vy, scale: vs } = viewport;
      const startX = Math.floor(-vx / vs / GRID_SIZE) * GRID_SIZE;
      const endX = Math.ceil((windowSize.width - vx) / vs / GRID_SIZE) * GRID_SIZE;
      const startY = Math.floor(-vy / vs / GRID_SIZE) * GRID_SIZE;
      const endY = Math.ceil((windowSize.height - vy) / vs / GRID_SIZE) * GRID_SIZE;
      const lines: React.ReactElement[] = [];
      for (let x = startX; x <= endX; x += GRID_SIZE) {
        const sx = x * vs + vx;
        lines.push(
          <Line key={`v${x}`} points={[sx, 0, sx, windowSize.height]} stroke={palette.gridLine} strokeWidth={1} listening={false} />,
        );
      }
      for (let y = startY; y <= endY; y += GRID_SIZE) {
        const sy = y * vs + vy;
        lines.push(
          <Line key={`h${y}`} points={[0, sy, windowSize.width, sy]} stroke={palette.gridLine} strokeWidth={1} listening={false} />,
        );
      }
      return lines;
    }, [viewport, windowSize.width, windowSize.height, palette.gridLine]);

    return (
      <div ref={ref} className="absolute inset-0" data-canvas-area data-tour="canvas">
        <Stage
          ref={stageRef}
          width={windowSize.width}
          height={windowSize.height}
          style={{ cursor: stageCursor }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          onWheel={onWheel}
          onContextMenu={(e) => e.evt.preventDefault()}
        >
          <Layer>
            {gridLines}

            <Group x={viewport.x} y={viewport.y} scaleX={viewport.scale} scaleY={viewport.scale}>
              {children}

              {/* Marquee selection rect */}
              {marqueeRect && (
                <Rect
                  x={marqueeRect.x} y={marqueeRect.y}
                  width={marqueeRect.w} height={marqueeRect.h}
                  fill="rgba(139,92,246,0.08)"
                  stroke="#8b5cf6"
                  strokeWidth={1 / viewport.scale}
                  dash={[4 / viewport.scale, 4 / viewport.scale]}
                  listening={false}
                />
              )}
              {lassoPoints && lassoPoints.length >= 4 && (
                <Line
                  points={lassoPoints.length >= 6
                    ? [...lassoPoints, lassoPoints[0], lassoPoints[1]]
                    : lassoPoints}
                  fill="rgba(139,92,246,0.08)"
                  stroke="#8b5cf6"
                  strokeWidth={1.5 / viewport.scale}
                  dash={[4 / viewport.scale, 4 / viewport.scale]}
                  closed={lassoPoints.length >= 6}
                  listening={false}
                />
              )}
              {frameDrawRect && (
                <Rect
                  x={frameDrawRect.x} y={frameDrawRect.y}
                  width={frameDrawRect.w} height={frameDrawRect.h}
                  fill={drawRectKind === 'box' ? 'rgba(99,102,241,0.10)' : 'rgba(251,191,36,0.06)'}
                  stroke={drawRectKind === 'box' ? '#6366f1' : '#f59e0b'}
                  strokeWidth={2 / viewport.scale}
                  dash={[6 / viewport.scale, 3 / viewport.scale]}
                  listening={false}
                />
              )}
              {markupStroke && (
                <Arrow
                  points={[markupStroke.x1, markupStroke.y1, markupStroke.x2, markupStroke.y2]}
                  stroke="#94a3b8"
                  fill="#94a3b8"
                  strokeWidth={2 / viewport.scale}
                  pointerLength={markupStroke.kind === 'line' ? 0 : 12 / viewport.scale}
                  pointerWidth={markupStroke.kind === 'line' ? 0 : 10 / viewport.scale}
                  pointerAtEnding={markupStroke.kind !== 'line'}
                  listening={false}
                />
              )}
            </Group>
          </Layer>
        </Stage>
      </div>
    );
  },
);
